import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/api/client";
import Layout from "@/components/Layout";
import Modal from "@/components/Modal";
import { productLabel, euro } from "@/lib/format";
import { toast } from "sonner";
import { IDS } from "@/constants/testIds";

export default function Dashboard() {
  const nav = useNavigate();
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [variants, setVariants] = useState([]);
  const [movementModal, setMovementModal] = useState(false);
  const [productModal, setProductModal] = useState(false);
  const [supplierModal, setSupplierModal] = useState(false);
  const [pForm, setPForm] = useState(initialProduct());
  const [sForm, setSForm] = useState({ name: "", contact: "", phone: "", email: "", web: "", note: "" });
  const [mForm, setMForm] = useState({ variant_id: "", type: "Carico", qty: 1, note: "" });

  const load = async () => {
    const [p, s, v] = await Promise.all([
      api.get("/products"), api.get("/suppliers"), api.get("/variants"),
    ]);
    setProducts(p.data); setSuppliers(s.data); setVariants(v.data);
  };
  useEffect(() => { load(); }, []);

  const totalStock = variants.reduce((a, v) => a + Number(v.qty || 0), 0);
  const totalStockValue = variants.reduce((a, v) => {
    const p = products.find((x) => x.id === v.product_id);
    return a + Number(p?.cost || 0) * Number(v.qty || 0);
  }, 0);
  const lowRows = variants.filter((v) => Number(v.qty) <= Number(v.min || 0));

  const saveProduct = async () => {
    if (!pForm.code.trim() || !pForm.name.trim()) return toast.error("Compila codice e nome");
    try {
      await api.post("/products", { ...pForm, cost: +pForm.cost || 0, price: +pForm.price || 0 });
      toast.success("Prodotto aggiunto");
      setProductModal(false); setPForm(initialProduct()); load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };
  const saveSupplier = async () => {
    if (!sForm.name.trim()) return toast.error("Inserisci la ragione sociale");
    try {
      await api.post("/suppliers", sForm);
      toast.success("Fornitore aggiunto");
      setSupplierModal(false); setSForm({ name: "", contact: "", phone: "", email: "", web: "", note: "" }); load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  const saveMovement = async () => {
    if (!mForm.variant_id) return toast.error("Seleziona una variante");
    try {
      await api.post("/movements", { ...mForm, qty: Math.abs(+mForm.qty || 0) });
      toast.success("Movimento registrato");
      setMovementModal(false); setMForm({ variant_id: "", type: "Carico", qty: 1, note: "" }); load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  return (
    <Layout topbarActions={
      <button className="btn accent" onClick={() => setProductModal(true)} data-testid={IDS.newProductBtn}>
        <i className="fa-solid fa-plus" /> Nuovo prodotto
      </button>
    }>
      <div className="hero">
        <div>
          <h2>Benvenuto in GymERP</h2>
          <p>Gestisci prodotti, varianti, fornitori, taglie, quantità, personalizzazioni e ordini da un unico spazio. I dati sono salvati in modo sicuro sul server.</p>
        </div>
        <div className="hero-badge">
          <small>Valore totale magazzino</small>
          <strong data-testid="stat-stock-value">{euro(totalStockValue)}</strong>
        </div>
      </div>

      <div className="grid cards">
        <div className="card"><div className="label">Prodotti registrati</div><div className="value" data-testid="stat-products">{products.length}</div><div className="hint">Modelli in anagrafica</div></div>
        <div className="card"><div className="label">Varianti a magazzino</div><div className="value" data-testid="stat-variants">{variants.length}</div><div className="hint">Combinazioni modello/taglia</div></div>
        <div className="card"><div className="label">Pezzi disponibili</div><div className="value" data-testid="stat-stock">{totalStock}</div><div className="hint">Quantità totale</div></div>
        <div className="card"><div className="label">Sotto scorta</div><div className="value" data-testid="stat-low">{lowRows.length}</div><div className="hint">Varianti da riordinare</div></div>
      </div>

      <div className="panel">
        <div className="panel-head"><h3>Azioni rapide</h3></div>
        <div className="panel-body quick-grid">
          <div className="quick">
            <h4>Anagrafica prodotto</h4>
            <p>Crea liberamente prodotti e modelli man mano che li acquisti.</p>
            <button className="btn primary" onClick={() => setProductModal(true)}><i className="fa-solid fa-plus" /> Aggiungi prodotto</button>
          </div>
          <div className="quick">
            <h4>Fornitore</h4>
            <p>Registra contatti e riferimenti dei tuoi fornitori.</p>
            <button className="btn" onClick={() => setSupplierModal(true)}><i className="fa-solid fa-truck" /> Aggiungi fornitore</button>
          </div>
          <div className="quick">
            <h4>Carico merce</h4>
            <p>Aumenta la disponibilità di una variante già censita.</p>
            <button className="btn" onClick={() => setMovementModal(true)}><i className="fa-solid fa-arrow-down" /> Registra carico</button>
          </div>
        </div>
      </div>

      <div className="panel">
        <div className="panel-head">
          <h3>Scorte da controllare</h3>
          <button className="btn small" onClick={() => nav("/magazzino")}><i className="fa-solid fa-arrow-right" /> Apri magazzino</button>
        </div>
        {lowRows.length ? (
          <div className="table-wrap">
            <table>
              <thead><tr><th>Prodotto</th><th>Variante</th><th>Disponibilità</th><th>Minimo</th></tr></thead>
              <tbody>
                {lowRows.slice(0, 6).map((v) => (
                  <tr key={v.id}>
                    <td>{productLabel(products, v.product_id)}</td>
                    <td>{v.color || "—"} / {v.size || "—"}</td>
                    <td className="stock-low">{v.qty}</td>
                    <td>{v.min}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty">Nessuna scorta critica.</div>
        )}
      </div>

      {/* Product modal */}
      <Modal open={productModal} title="Nuovo prodotto / modello" onClose={() => setProductModal(false)} onSave={saveProduct}>
        <div className="form-grid">
          <div className="field"><label>Codice prodotto *</label><input value={pForm.code} onChange={(e) => setPForm({ ...pForm, code: e.target.value })} placeholder="es. BODY-001" data-testid="f-code" /></div>
          <div className="field"><label>Nome modello *</label><input value={pForm.name} onChange={(e) => setPForm({ ...pForm, name: e.target.value })} placeholder="es. Aurora" data-testid="f-name" /></div>
          <div className="field"><label>Categoria</label><input value={pForm.category} onChange={(e) => setPForm({ ...pForm, category: e.target.value })} placeholder="Body, tuta, accessorio..." /></div>
          <div className="field"><label>Fornitore</label>
            <select value={pForm.supplier_id} onChange={(e) => setPForm({ ...pForm, supplier_id: e.target.value })}>
              <option value="">Nessuno</option>
              {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div className="field"><label>Taglie disponibili</label><input value={pForm.sizes} onChange={(e) => setPForm({ ...pForm, sizes: e.target.value })} placeholder="es. 4, 6, 8, 10, XS, S, M" /></div>
          <div className="field"><label>Colori disponibili</label><input value={pForm.colors} onChange={(e) => setPForm({ ...pForm, colors: e.target.value })} placeholder="es. Nero, Rosa, Blu" /></div>
          <div className="field"><label>Costo unitario</label><input type="number" step="0.01" min="0" value={pForm.cost} onChange={(e) => setPForm({ ...pForm, cost: e.target.value })} /></div>
          <div className="field"><label>Prezzo vendita</label><input type="number" step="0.01" min="0" value={pForm.price} onChange={(e) => setPForm({ ...pForm, price: e.target.value })} /></div>
          <div className="field full"><label>Descrizione / note</label><textarea rows="3" value={pForm.description} onChange={(e) => setPForm({ ...pForm, description: e.target.value })} /></div>
        </div>
      </Modal>

      {/* Supplier modal */}
      <Modal open={supplierModal} title="Nuovo fornitore" onClose={() => setSupplierModal(false)} onSave={saveSupplier}>
        <div className="form-grid">
          <div className="field full"><label>Ragione sociale *</label><input value={sForm.name} onChange={(e) => setSForm({ ...sForm, name: e.target.value })} data-testid="s-name" /></div>
          <div className="field"><label>Referente</label><input value={sForm.contact} onChange={(e) => setSForm({ ...sForm, contact: e.target.value })} /></div>
          <div className="field"><label>Telefono</label><input value={sForm.phone} onChange={(e) => setSForm({ ...sForm, phone: e.target.value })} /></div>
          <div className="field"><label>Email</label><input type="email" value={sForm.email} onChange={(e) => setSForm({ ...sForm, email: e.target.value })} /></div>
          <div className="field"><label>Sito web</label><input value={sForm.web} onChange={(e) => setSForm({ ...sForm, web: e.target.value })} /></div>
          <div className="field full"><label>Note</label><textarea rows="3" value={sForm.note} onChange={(e) => setSForm({ ...sForm, note: e.target.value })} /></div>
        </div>
      </Modal>

      {/* Movement modal */}
      <Modal open={movementModal} title="Movimento di magazzino" onClose={() => setMovementModal(false)} onSave={saveMovement}>
        {variants.length ? (
          <div className="form-grid">
            <div className="field full"><label>Variante *</label>
              <select value={mForm.variant_id} onChange={(e) => setMForm({ ...mForm, variant_id: e.target.value })}>
                <option value="">— seleziona —</option>
                {variants.map((v) => <option key={v.id} value={v.id}>{productLabel(products, v.product_id)} · {v.color || "N/D"} · {v.size || "N/D"} (SKU {v.sku})</option>)}
              </select>
            </div>
            <div className="field"><label>Tipo</label>
              <select value={mForm.type} onChange={(e) => setMForm({ ...mForm, type: e.target.value })}>
                <option>Carico</option><option>Scarico</option>
                <option>Rettifica positiva</option><option>Rettifica negativa</option>
              </select>
            </div>
            <div className="field"><label>Quantità *</label><input type="number" min="1" value={mForm.qty} onChange={(e) => setMForm({ ...mForm, qty: e.target.value })} /></div>
            <div className="field full"><label>Nota</label><textarea rows="3" value={mForm.note} onChange={(e) => setMForm({ ...mForm, note: e.target.value })} /></div>
          </div>
        ) : <div className="empty">Prima crea almeno una variante di magazzino.</div>}
      </Modal>
    </Layout>
  );
}

function initialProduct() {
  return { code: "", name: "", category: "", supplier_id: "", sizes: "", colors: "", cost: 0, price: 0, description: "" };
}
